import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Animated,
  BackHandler,
  Image,
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import LogoWordmark from '../components/LogoWordmark';
import BlobButton, {
  BLOB_HEIGHT,
  BLOB_HEIGHT_SHORT,
} from '../components/onboarding/BlobButton';
import PageDashes from '../components/onboarding/PageDashes';
import Text from '../components/ui/Text';
import { onboardingSlides } from '../data/onboarding';
import {
  colors,
  MAX_CONTENT_WIDTH,
  radii,
  screenPadding,
  SHORT_HEIGHT,
  spacing,
  useResponsive,
} from '../theme';

/**
 * Three-slide onboarding carousel.
 *
 * Swipe or tap the corner blob to advance; "Skip" and the final "Get Started"
 * both call `onDone`. The screen holds no persistence of its own - whoever
 * mounts it decides whether onboarding has been seen before (see App.tsx).
 *
 * Layout: the pager owns only the artwork and the copy. The header, the page
 * dashes and the blob are chrome pinned to the screen, so they stay put while
 * the slides move under them. That is also why the bottom safe-area inset is
 * applied by hand rather than by `SafeAreaView` - the blob has to bleed under
 * the home indicator to sit flush in the corner.
 */

/**
 * Margin between the bottom of the copy and the top of the blob.
 *
 * The clearance the copy actually reserves is this plus the blob's own height,
 * computed per device below. It used to be a single hard 120 - correct only
 * while the blob was always 98 tall, and 120 of a 390pt-tall landscape screen
 * is a third of the display given to a gap.
 */
const BLOB_GAP = 22;

/**
 * How far above the bottom of the screen the chrome stops.
 *
 * Android's navigation bar can be translucent, so anything drawn to the
 * physical bottom edge shows through it - that is why the blob ends here
 * rather than bleeding into the corner. `insets.bottom` is the bar's height
 * when the window is edge-to-edge and 0 when it is not, so the floor covers
 * the second case.
 */
const MIN_BOTTOM_GAP = 16;

/** Distance from the gutter up to the dashes, chosen so they line up with the
 * centre of the button's arrow (16pt margin + half of its 52pt height). */
const DASH_OFFSET = 38;

/** Breathing room between the artwork and the headline. */
const ART_GAP = spacing.sm;

/**
 * Header height, and the trimmed version used on a screen under 700pt tall.
 *
 * The wordmark alone is short, so the tall lockup header is no longer needed -
 * but it stays roomier than the glyph so "Skip" has a real target. On a short
 * screen the 12pt is worth more to the artwork than to the air around the
 * wordmark; 48 is the floor, because "Skip" is a control.
 */
const HEADER_HEIGHT = 60;
const HEADER_HEIGHT_SHORT = 48;

/**
 * How much vertical room the chrome takes, for a given window height.
 *
 * Exported for the same reason `splashGeometry` is: this is the arithmetic
 * that decides whether the illustration has any room left, and a mounted test
 * cannot see it. Kept beside the component rather than in the theme because
 * these two numbers only mean anything together, and only here.
 */
export const onboardingChrome = (height: number) => {
  const short = height < SHORT_HEIGHT;
  const blobHeight = short ? BLOB_HEIGHT_SHORT : BLOB_HEIGHT;

  return {
    short,
    blobHeight,
    /** Reserved below the copy, so it clears the corner rather than sitting on it. */
    blobClearance: blobHeight + BLOB_GAP,
    headerHeight: short ? HEADER_HEIGHT_SHORT : HEADER_HEIGHT,
  };
};

type Props = {
  onDone: () => void;
};

function OnboardingScreen({ onDone }: Props) {
  const resp = useResponsive();
  const { width } = resp;
  const insets = useSafeAreaInsets();
  const bottomGutter = Math.max(insets.bottom, MIN_BOTTOM_GAP);

  /*
   * The corner shrinks on a short screen, and the copy's reserved space
   * follows it.
   *
   * Everything on this screen is stacked vertically - header, artwork,
   * headline, rule, body - and the only one of them that can give is the
   * artwork, because the rest is text at a fixed ramp. So on a screen under
   * 700pt the chrome gives up what it can first: 26pt from the blob and 12pt
   * from the header. Both go straight back to the illustration, which is
   * otherwise the thing that gets crushed.
   *
   * On the reference phone the clearance is 98 + 22 = 120, exactly the
   * constant it replaced.
   */
  const { blobHeight, blobClearance, headerHeight } = onboardingChrome(
    resp.height,
  );

  /*
   * Two panes when there is genuinely room for two.
   *
   * The portrait composition - artwork above, copy below - is drawn for a
   * screen taller than it is wide. On a desktop browser or a landscape
   * tablet that stacking leaves the copy below the fold and the art alone on
   * a huge stage, which reads as a blown-up phone. Side by side, each half
   * gets a shape it was actually designed for: the copy sits centred in its
   * own column and the artwork keeps `contain` - scaled, never cropped.
   * Nothing about the portrait layout changes on a phone.
   */
  const wide = resp.isTablet && resp.isLandscape;

  const scrollRef = useRef<ScrollView>(null);
  const scrollX = useRef(new Animated.Value(0)).current;
  const [index, setIndex] = useState(0);

  /*
   * Measured height of the artwork area.
   *
   * The panels are sized in JS rather than with `aspectRatio` on the image.
   * Yoga treats `aspectRatio` as a constraint in both directions, so pairing
   * it with `maxHeight` makes a clamped height push the width back out past
   * the screen - the artwork ends up larger than the stage and hangs off it.
   * Doing the arithmetic here keeps both dimensions under our control.
   */
  const [stageHeight, setStageHeight] = useState(0);
  const onStageLayout = useCallback((event: LayoutChangeEvent) => {
    const measured = event.nativeEvent.layout.height;
    setStageHeight(prev => (Math.abs(prev - measured) > 0.5 ? measured : prev));
  }, []);

  /**
   * Mirrors `index` for callbacks that must not re-run when it changes - the
   * rotation handler below would otherwise fight the animated scroll.
   */
  const indexRef = useRef(0);
  const setPage = useCallback((next: number) => {
    indexRef.current = next;
    setIndex(next);
  }, []);

  const lastIndex = onboardingSlides.length - 1;
  const isLast = index === lastIndex;

  const goNext = useCallback(() => {
    if (indexRef.current >= lastIndex) {
      onDone();
      return;
    }
    const next = indexRef.current + 1;
    setPage(next);
    scrollRef.current?.scrollTo({ x: next * width, animated: true });
  }, [lastIndex, onDone, setPage, width]);

  const onMomentumEnd = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      setPage(Math.round(event.nativeEvent.contentOffset.x / width));
    },
    [setPage, width],
  );

  /*
   * Back steps to the previous slide instead of leaving.
   *
   * The pager is one screen with an index, so Android has nothing to pop and
   * a press on slide three closed the app. On the first slide there is
   * genuinely nothing behind us, so `false` lets it leave as normal.
   */
  useEffect(() => {
    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      if (indexRef.current <= 0) {
        return false;
      }
      const previous = indexRef.current - 1;
      setPage(previous);
      scrollRef.current?.scrollTo({ x: previous * width, animated: true });
      return true;
    });
    return () => sub.remove();
  }, [setPage, width]);

  // A rotation changes the page width, which would leave the pager parked
  // between two slides. Snap back to the current one.
  const lastWidth = useRef(width);
  useEffect(() => {
    if (lastWidth.current === width) {
      return;
    }
    lastWidth.current = width;
    scrollRef.current?.scrollTo({ x: indexRef.current * width, animated: false });
  }, [width]);

  return (
    <SafeAreaView style={styles.root} edges={['top', 'left', 'right']}>
      <View style={[styles.header, { height: headerHeight }]}>
        <LogoWordmark height={20} />

        {/* A filled pill rather than bare text. On a page whose whole job is
            to move you forward, the one way out has to look like a control -
            two words in the corner read as a caption. Muted fill and muted
            ink, so it is unmistakably the quieter of the two actions. */}
        <Pressable
          onPress={onDone}
          hitSlop={12}
          accessibilityRole="button"
          accessibilityLabel="Skip onboarding"
          style={({ pressed }) => [styles.skip, pressed ? styles.skipPressed : null]}
        >
          <Text variant="eyebrow" color={colors.inkMuted} style={styles.skipText}>
            Skip
          </Text>
        </Pressable>
      </View>

      <Animated.ScrollView
        ref={scrollRef}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={Animated.event(
          [{ nativeEvent: { contentOffset: { x: scrollX } } }],
          { useNativeDriver: true },
        )}
        onMomentumScrollEnd={onMomentumEnd}
      >
        {onboardingSlides.map((slide, i) => {
          /*
           * Every step is a transition, not a cut. The two halves drift at
           * their own rate, the artwork also scales, and the copy lifts - all
           * keyed off scroll position, so a tap (which scrolls with
           * `animated: true`) and a swipe play the same motion.
           */
          const range = [(i - 1) * width, i * width, (i + 1) * width];
          const drift = (distance: number) =>
            scrollX.interpolate({
              inputRange: range,
              outputRange: [distance, 0, -distance],
              extrapolate: 'clamp',
            });
          const fade = scrollX.interpolate({
            inputRange: range,
            outputRange: [0, 1, 0],
            extrapolate: 'clamp',
          });
          const zoom = scrollX.interpolate({
            inputRange: range,
            outputRange: [0.82, 1, 0.82],
            extrapolate: 'clamp',
          });
          const lift = scrollX.interpolate({
            inputRange: range,
            outputRange: [28, 0, 28],
            extrapolate: 'clamp',
          });

          /*
           * Fit the panel to the stage, then take the width back from the
           * ratio. Every panel therefore ends flush with the bottom of the
           * stage regardless of its shape - only the top edge varies.
           *
           * The window-derived cap alongside the measured stage is
           * load-bearing in the wide layout. There the page's height comes
           * from its tallest pane, so an unclamped first frame - stage not
           * yet measured, art at its natural height, which at a desktop
           * width is taller than the screen - becomes the page's min-height,
           * the stage then MEASURES that oversized page, and the clamp never
           * engages: a feedback loop that left the copy centred somewhere
           * below the fold. The cap breaks the loop with a bound the layout
           * cannot inflate. In the portrait layout the flex chain bounds the
           * stage below the cap, so the cap simply never binds there.
           */
          const hardCap = Math.max(
            0,
            resp.height -
              headerHeight -
              ART_GAP -
              (wide ? blobClearance + bottomGutter : 0),
          );
          const available =
            stageHeight > 0
              ? Math.min(Math.max(0, stageHeight - ART_GAP), hardCap)
              : hardCap;
          const naturalHeight = width / slide.aspectRatio;
          let artHeight =
            available > 0 ? Math.min(naturalHeight, available) : naturalHeight;
          let artWidth = artHeight * slide.aspectRatio;

          /*
           * In the two-pane layout the art also answers to its pane's WIDTH.
           * Height-fit alone let a squarish panel out-grow the half-screen
           * pane, and the overflow clipped off its left edge - which on
           * slide one is where the feature badges sit. Width wins ties: a
           * smaller, whole illustration over a taller, beheaded one.
           */
          if (wide && artWidth > width / 2) {
            artWidth = width / 2;
            artHeight = artWidth / slide.aspectRatio;
          }

          return (
            <View
              key={slide.key}
              /* `row-reverse` keeps the JSX order (art first) while putting
                 the art on the RIGHT, where every panel's subject is. */
              style={[styles.page, { width }, wide ? styles.pageWide : null]}
            >
              <Animated.View
                onLayout={onStageLayout}
                style={[
                  styles.stage,
                  /* The art pane clears the corner blob, which now overlaps
                     its bottom edge rather than sitting below the copy. */
                  wide ? { paddingBottom: blobClearance + bottomGutter } : null,
                  {
                    opacity: fade,
                    transform: [
                      { translateX: drift(width * 0.22) },
                      { scale: zoom },
                    ],
                  },
                ]}
              >
                <Image
                  source={slide.image}
                  /* The stage is bottom-aligned, so margin below the image is
                     what raises it. */
                  style={{
                    width: artWidth,
                    height: artHeight,
                    marginBottom: slide.lift ?? 0,
                  }}
                  resizeMode="contain"
                  accessible
                  accessibilityRole="image"
                  accessibilityLabel={slide.imageLabel}
                />
              </Animated.View>

              <Animated.View
                style={[
                  styles.copy,
                  wide
                    ? styles.copyWide
                    : { paddingBottom: blobClearance + bottomGutter },
                  {
                    opacity: fade,
                    transform: [
                      { translateX: drift(width * 0.08) },
                      { translateY: lift },
                    ],
                  },
                ]}
              >
                <Text variant="display">{slide.headline}</Text>
                <View style={styles.rule} />
                <Text variant="body" color={colors.inkMuted}>
                  {slide.body}
                </Text>
              </Animated.View>
            </View>
          );
        })}
      </Animated.ScrollView>

      <PageDashes
        count={onboardingSlides.length}
        index={index}
        style={[styles.dashes, { bottom: bottomGutter + DASH_OFFSET }]}
      />

      <BlobButton
        label={isLast ? 'Get Started' : undefined}
        onPress={goNext}
        bottomGutter={bottomGutter}
        accessibilityLabel={isLast ? 'Get started' : 'Next slide'}
        pageIndex={index}
        height={blobHeight}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  /* Height is set per device by `onboardingChrome`. */
  header: {
    paddingHorizontal: screenPadding,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  /* Sized by its own word plus generous side padding, so the pill stays a
     pill rather than stretching into a bar as the label changes. */
  skip: {
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.sm,
    borderRadius: radii.pill,
    backgroundColor: colors.surfaceMuted,
  },
  /* The reference sets its letters apart; `eyebrow` already tracks at 1.1, so
     this only widens it enough to read as a deliberate label. */
  skipText: {
    letterSpacing: 1.6,
  },
  skipPressed: {
    opacity: 0.6,
  },

  /*
   * Width only, no `flex`. Inside a horizontal ScrollView the page is a row
   * child, so `flex: 1` would set `flexBasis: 0` on the horizontal axis and
   * override the width that makes paging work. Height comes from the content
   * container's default `alignItems: 'stretch'`.
   */
  page: {},
  /*
   * No horizontal padding: in the design the illustration runs to both edges
   * of the panel (the shrubs on slide 1, the room on slide 3), so the artwork
   * gets the full screen width and only the copy below it is guttered.
   */
  /*
   * Artwork is pinned to the right, not centred. The panels are cut with their
   * subject on the right and open space on the left (slide 1's badges sit in
   * it), so centring left a visible gutter down the right edge while the
   * subject floated inboard.
   */
  stage: {
    flex: 1,
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    paddingBottom: ART_GAP,
    // The measured size lands a frame late; clip until it does.
    overflow: 'hidden',
  },
  /*
   * The pager itself stays full-bleed on every device - a first-run screen is
   * artwork and it should own the whole display. Only the copy is held back:
   * a headline and two lines of body set across a 1024pt tablet are a line
   * length nobody can track, and the eye loses the start of the next line.
   * The cap never binds on a phone, where the screen is already narrower.
   */
  copy: {
    width: '100%',
    maxWidth: MAX_CONTENT_WIDTH + screenPadding * 2,
    paddingHorizontal: screenPadding,
  },
  /* Side by side with the artwork - see `wide`. The width cap above keeps
     the line length readable; centring does the rest. */
  pageWide: {
    flexDirection: 'row-reverse',
    alignItems: 'stretch',
  },
  copyWide: {
    flex: 1,
    width: 'auto',
    justifyContent: 'center',
    paddingHorizontal: screenPadding * 2,
    paddingBottom: 0,
  },
  /** Short sage rule separating the headline from the body copy. */
  rule: {
    width: 28,
    height: 2,
    borderRadius: 1,
    backgroundColor: colors.borderStrong,
    marginTop: spacing.xl,
    marginBottom: spacing.md,
  },

  dashes: {
    position: 'absolute',
    left: screenPadding,
  },
});

export default OnboardingScreen;
